// ResponsibilityAssignmentRow — one responsibility's assignment state at a scope,
// for a detail/peek list. Reusable across zone / cluster / team / site / warehouse.
//
// Four states made legible without reading text:
//  • ASSIGNED (required or not) — the lead's avatar + "Name · +N backups". Calm;
//    no chip (a satisfied responsibility doesn't need a "Required" flag).
//  • REQUIRED + UNASSIGNED — the actionable gap: a dashed amber slot, an amber
//    "Required" chip (warning = needs attention, NOT danger), amber "Not assigned",
//    and an amber inline-start accent so it scans in a stack.
//  • OPTIONAL + UNASSIGNED — a muted dashed slot + muted "Not assigned". No alarm.
// The leading slot answers "is anyone on this?"; the amber treatment answers
// "is a REQUIRED one still open?".
//
// The PEOPLE in a row are CONTENT — each a reference the host may render as the
// person (`renderMember`: an `EntityRef` that peeks them — ADR-036 §6); the row
// itself is a door only inside the chains EDITOR (`onManage` — pick a chain to
// edit). On an entity's card the rows are never doors: the card's own act
// (Manage responsibilities) is the one way in (architect-ruled at the team's
// live look), so a press on a person opens the person, not the editor.

import type { ReactNode } from 'react';
import { Avatar } from '../ui/Avatar.js';
import { Badge } from '../ui/Badge.js';
import { icons } from '../ui/icons.js';

export interface ResponsibilityAssignmentRowLabels {
  /** Chip shown only on a required gap. */
  required: string;
  /** Subtitle when no one is assigned. */
  notAssigned: string;
}

/** One person on a chain — the id the host may turn into a reference, the name the row shows. */
export interface ResponsibilityAssignmentMember {
  id: string;
  name: string;
}

export interface ResponsibilityAssignmentRowProps {
  name: string;
  required: boolean;
  /** Assignees in chain order (position 1 = lead); empty = unassigned. */
  members: ResponsibilityAssignmentMember[];
  /** A person as the host renders them (a reference to the person); default: the name as text. */
  renderMember?: (member: ResponsibilityAssignmentMember) => ReactNode;
  /** The chains EDITOR's list: the row opens that chain's stage. Never on an entity's card. */
  onManage?: () => void;
  labels: ResponsibilityAssignmentRowLabels;
}

export function ResponsibilityAssignmentRow({
  name,
  required,
  members,
  renderMember,
  onManage,
  labels,
}: ResponsibilityAssignmentRowProps) {
  const unfilled = members.length === 0;
  const gap = required && unfilled; // required + unassigned = the actionable gap

  const leading = members.length > 0 ? (
    <Avatar name={members[0]!.name} size="sm" />
  ) : (
    <span
      aria-hidden
      className={`inline-flex h-6 w-6 shrink-0 rounded-full border-2 border-dashed ${
        gap ? 'border-[var(--color-warning)]' : 'border-[var(--color-border-strong)]'
      }`}
    />
  );

  const body = (
    <>
      {leading}
      <span className="flex flex-1 flex-col gap-0.5 min-w-0">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-[var(--color-text-primary)]">{name}</span>
          {gap && (
            <Badge variant="warning" appearance="subtle">
              {labels.required}
            </Badge>
          )}
        </span>
        {unfilled ? (
          <span className={`text-xs ${gap ? 'text-[var(--color-warning)]' : 'text-[var(--color-text-disabled)]'}`}>
            {labels.notAssigned}
          </span>
        ) : (
          // Each assignee on its own line (lead first) — clearer for viewers who
          // can't open the editor to expand a "+N others".
          <span className="flex flex-col gap-0.5">
            {members.map((m, i) => (
              <span key={`${i}-${m.id}`} className="flex min-w-0 text-xs text-[var(--color-text-secondary)]">
                {renderMember ? renderMember(m) : <span className="truncate">{m.name}</span>}
              </span>
            ))}
          </span>
        )}
      </span>
    </>
  );

  const frame = `flex w-full items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2.5 ${
    gap ? 'border-s-2 border-s-[var(--color-warning)]' : ''
  }`;

  return onManage ? (
    <button
      type="button"
      onClick={onManage}
      className={`${frame} text-start hover:bg-[var(--color-bg-hover)] outline-none focus-ring cursor-pointer`}
    >
      {body}
      <icons.caretRight aria-hidden size={16} className="shrink-0 text-[var(--color-text-secondary)]" />
    </button>
  ) : (
    <div className={frame}>{body}</div>
  );
}
